<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatCount, formatMoney } from '../utils/format'
import type { EmployeeOption, PayrollEmployeeStatement, PayrollPeriod } from '../types/finance'

/**
 * `/payroll/statement` — one employee's payroll history: earnings, deductions
 * and postings for a period/currency window. `employee_id` is required by the
 * report endpoint, so the page starts in a "pick an employee" state.
 */

const { t } = useI18n()
const { listPayrollPeriods, payrollEmployeeStatement } = useFinanceApi()

const periods = ref<PayrollPeriod[]>([])
const employee = ref<EmployeeOption | null>(null)
const statement = ref<PayrollEmployeeStatement | null>(null)
const loading = ref(false)
const error = ref('')
const pickerOpen = ref(false)

const filters = ref({
  payroll_period_id: '' as string | number,
  currency_code: '',
  date_from: '',
  date_to: '',
})

const invalidRange = computed(() =>
  Boolean(filters.value.date_from && filters.value.date_to && filters.value.date_from > filters.value.date_to),
)

const earnings = computed(() => statement.value?.earnings ?? [])
const deductions = computed(() => statement.value?.deductions ?? [])
const postings = computed(() => statement.value?.postings ?? [])

const earningTotal = computed(() =>
  earnings.value.reduce((sum, row) => sum + Number(row.gross_amount ?? 0), 0),
)
const deductionTotal = computed(() =>
  deductions.value.reduce((sum, row) => sum + Number(row.amount ?? 0), 0),
)
const postedTotal = computed(() =>
  postings.value
    .filter((row) => row.status !== 'reversed')
    .reduce((sum, row) => sum + Number(row.net_amount ?? 0), 0),
)
const currency = computed(
  () => statement.value?.filters?.currency_code || filters.value.currency_code || earnings.value[0]?.currency_code || '',
)

async function load() {
  if (!employee.value) {
    statement.value = null
    return
  }
  if (invalidRange.value) {
    error.value = t('finance.common.invalidRange')
    return
  }
  loading.value = true
  error.value = ''
  try {
    statement.value = await payrollEmployeeStatement({
      employee_id: employee.value.id,
      payroll_period_id: filters.value.payroll_period_id,
      currency_code: filters.value.currency_code.trim() || undefined,
      date_from: filters.value.date_from || undefined,
      date_to: filters.value.date_to || undefined,
    })
  } catch (cause) {
    error.value = formatApiError(cause)
    statement.value = null
  } finally {
    loading.value = false
  }
}

async function loadPeriods() {
  try {
    const result = await listPayrollPeriods({ page: 1, per_page: 50 })
    periods.value = result.items
  } catch {
    periods.value = []
  }
}

function selectEmployee(selected: EmployeeOption) {
  employee.value = selected
  pickerOpen.value = false
  void load()
}

onMounted(() => {
  void loadPeriods()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.statement.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.statement.title') }}</h2>
      <p>{{ t('finance.payrollFlow.statement.description') }}</p>
    </div>
    <RouterLink class="btn-secondary" to="/payroll/reports">
      {{ t('finance.payrollFlow.reports.title') }}
    </RouterLink>
  </div>

  <div class="toolbar">
    <button class="btn-secondary" type="button" @click="pickerOpen = true">
      {{ employee ? employee.name : t('finance.payrollFlow.statement.selectEmployee') }}
    </button>
    <select v-model="filters.payroll_period_id" class="filter-select">
      <option value="">{{ t('finance.payrollFlow.allPeriods') }}</option>
      <option v-for="period in periods" :key="period.id" :value="period.id">
        {{ period.code }} · {{ period.name }}
      </option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <button class="btn-primary" type="button" :disabled="loading || !employee" @click="load">
      {{ t('finance.common.apply') }}
    </button>
  </div>

  <p v-if="invalidRange" class="error-inline">{{ t('finance.common.invalidRange') }}</p>
  <p v-else-if="error" class="error-inline">{{ error }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <p v-else-if="!employee" class="state-message">{{ t('finance.payrollFlow.statement.selectEmployee') }}</p>

  <template v-else-if="statement">
    <div class="summary-row">
      <article class="summary-card accent">
        <span class="summary-value">{{ formatMoney(earningTotal, currency) }}</span>
        <span class="summary-label">
          {{ t('finance.payrollFlow.runs.earnings') }} · {{ t('finance.common.rows', { count: earnings.length }) }}
        </span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatMoney(deductionTotal, currency) }}</span>
        <span class="summary-label">
          {{ t('finance.payrollFlow.runs.deductionLines') }} · {{ t('finance.common.rows', { count: deductions.length }) }}
        </span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatMoney(postedTotal, currency) }}</span>
        <span class="summary-label">
          {{ t('finance.payrollFlow.postings.net') }} · {{ t('finance.common.rows', { count: postings.length }) }}
        </span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatMoney(earningTotal - deductionTotal, currency) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.runs.net') }}</span>
      </article>
    </div>

    <h3>{{ t('finance.payrollFlow.runs.earnings') }}</h3>
    <p v-if="!earnings.length" class="empty-inline">{{ t('finance.payrollFlow.statement.noEarnings') }}</p>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.date') }}</th>
            <th>{{ t('finance.common.type') }}</th>
            <th>{{ t('finance.common.detail') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.quantity') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.accruals.rate') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.gross') }}</th>
            <th>{{ t('finance.payrollFlow.postings.run') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in earnings" :key="`e-${row.id}`">
            <td class="cell-date">{{ row.created_at ? formatDisplayDate(row.created_at) : '—' }}</td>
            <td>{{ row.earning_type }}</td>
            <td>{{ row.description || '—' }}</td>
            <td class="cell-num">
              {{ row.quantity === null || row.quantity === undefined ? '—' : `${formatCount(row.quantity)} ${row.unit || ''}` }}
            </td>
            <td class="cell-num">{{ formatMoney(row.rate, row.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(row.gross_amount, row.currency_code) }}</td>
            <td>
              <RouterLink class="link-action" :to="`/payroll/runs/${row.payroll_run_id}`">
                {{ row.payroll_run_code || row.payroll_run_id }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>{{ t('finance.payrollFlow.runs.deductionLines') }}</h3>
    <p v-if="!deductions.length" class="empty-inline">{{ t('finance.payrollFlow.statement.noDeductions') }}</p>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.date') }}</th>
            <th>{{ t('finance.common.type') }}</th>
            <th>{{ t('finance.common.detail') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.amount') }}</th>
            <th>{{ t('finance.payrollFlow.postings.run') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in deductions" :key="`d-${row.id}`">
            <td class="cell-date">{{ row.created_at ? formatDisplayDate(row.created_at) : '—' }}</td>
            <td>{{ row.deduction_type }}</td>
            <td>{{ row.description || '—' }}</td>
            <td class="cell-num">{{ formatMoney(row.amount, row.currency_code) }}</td>
            <td>
              <RouterLink class="link-action" :to="`/payroll/runs/${row.payroll_run_id}`">
                {{ row.payroll_run_code || row.payroll_run_id }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>{{ t('finance.payrollFlow.postings.title') }}</h3>
    <p v-if="!postings.length" class="empty-inline">{{ t('finance.payrollFlow.statement.noPostings') }}</p>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.postings.account') }}</th>
            <th>{{ t('finance.payrollFlow.postings.run') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.postings.net') }}</th>
            <th>{{ t('finance.payrollFlow.postings.postedAt') }}</th>
            <th>{{ t('finance.common.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in postings" :key="`p-${row.id}`">
            <td class="cell-name">
              {{ row.financial_account_name || '—' }}
              <span class="cell-sub">{{ row.financial_account_code || '—' }}</span>
            </td>
            <td>
              <RouterLink class="link-action" :to="`/payroll/runs/${row.payroll_run_id}`">
                {{ row.payroll_run_code || row.payroll_run_id }}
              </RouterLink>
            </td>
            <td class="cell-num">{{ formatMoney(row.net_amount, row.currency_code) }}</td>
            <td class="cell-date">{{ row.posted_at ? formatDisplayDateTime(row.posted_at) : '—' }}</td>
            <td>
              <span class="status-badge" :class="`status-${row.status}`">{{ row.status }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>

  <EmployeePickerModal
    :open="pickerOpen"
    :selected-id="employee?.id ?? null"
    @close="pickerOpen = false"
    @select="selectEmployee"
  />
</template>

<style scoped>
.status-posted,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
